import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Switch } from "../components/ui/switch";
import { Users, Plus, Upload, Camera, Edit, Trash2, Search, Globe, CheckCircle, XCircle, Image, Loader2, Calendar, FileText, CreditCard, Building2, AlertTriangle, Clock, Check, X, ClipboardList, Shield, Download, UserX, Lock, Eye, EyeOff, JapaneseYen, QrCode, Printer, MessageCircle } from "lucide-react";
import axios from "axios";
import { API, LANGUAGES, Flag } from "../App";
import { toast } from "sonner";
import * as faceapi from "face-api.js";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { usePlan } from "../components/AdminLayout";
import { QRCodeSVG } from "qrcode.react";

const DETECTION_OPTIONS = new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 });
// 高精度モデル用オプション
const SSD_OPTIONS = new faceapi.SsdMobilenetv1Options({ minConfidence: 0.5 });

const EmployeesPage = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showFaceDialog, setShowFaceDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [registeredFaces, setRegisteredFaces] = useState([]);
  const [faceLabel, setFaceLabel] = useState("");
  const [capturing, setCapturing] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [cameraFacing, setCameraFacing] = useState("user"); // "user" = フロント, "environment" = バック
  const [availableCameras, setAvailableCameras] = useState([]);
  
  // Plan context for feature gating
  const planData = usePlan();
  const faceAuthEnabled = planData?.features?.face_auth !== false;
  
  // Paid Leave & ID Document Management
  const [showPaidLeaveDialog, setShowPaidLeaveDialog] = useState(false);
  const [showIdDocDialog, setShowIdDocDialog] = useState(false);
  const [paidLeaveData, setPaidLeaveData] = useState({ granted: 0, used: 0, balance: 0 });
  const [idDocuments, setIdDocuments] = useState([]);
  const [savingPaidLeave, setSavingPaidLeave] = useState(false);
  const [savingIdDoc, setSavingIdDoc] = useState(false);
  const [newIdDoc, setNewIdDoc] = useState({ type: "在留カード", number: "", expiry: "", image_url: "" });
  const [companyName, setCompanyName] = useState("");
  
  // Leave Request Management
  const [activeTab, setActiveTab] = useState("employees");
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [showNewRequestDialog, setShowNewRequestDialog] = useState(false);
  const [newLeaveRequest, setNewLeaveRequest] = useState({
    employee_id: "",
    leave_type: "paid_leave",
    start_date: "",
    end_date: "",
    days: 1,
    reason: ""
  });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [processingRequest, setProcessingRequest] = useState(null);
  
  // マイナンバー表示/非表示の状態
  const [showMyNumber, setShowMyNumber] = useState(false);
  
  // QRコード管理
  const [showQrDialog, setShowQrDialog] = useState(false);
  const [qrEmployee, setQrEmployee] = useState(null);
  const [showBulkQrDialog, setShowBulkQrDialog] = useState(false);
  
  // 雇用形態マスタ
  const [employmentTypes, setEmploymentTypes] = useState([]);
  
  // 写真撮影用のState
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [photoType, setPhotoType] = useState(null); // 'profile', 'id', 'residence_front', 'residence_back'
  const [photoStream, setPhotoStream] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const photoVideoRef = useRef(null);
  const photoCanvasRef = useRef(null);
  const photoInputRef = useRef(null);
  
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const animationRef = useRef(null);
  const streamRef = useRef(null);

  const [formData, setFormData] = useState({
    employee_code: "",
    name: "",
    name_kana: "",
    language: "ja",
    department: "",
    position: "",
    employment_type: "regular",
    // Contact Info
    email: "",
    phone: "",
    whatsapp_number: "",
    address: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_relationship: "",
    // Personal Info
    birth_date: "",
    hire_date: "",
    termination_date: "",
    my_number: "",
    pension_number: "",
    health_insurance_number: "",
    employment_insurance_number: "",
    // Foreign Worker Info
    nationality: "",
    is_foreign_worker: false,
    residence_status: "",
    residence_period_end: "",
    residence_card_number: "",
    passport_number: "",
    passport_expiry: "",
    work_permission: "",
    designated_activities: "",
    // Leave
    paid_leave_balance: 10,
    // Photo fields
    profile_photo: "",
    id_photo_front: "",
    id_photo_back: "",
    drivers_license_front: "",
    drivers_license_back: "",
    residence_card_front: "",
    residence_card_back: "",
    resume_page1: "",
    resume_page2: ""
  });

  // モデル読み込み
  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = "/models";
        await Promise.all([
          faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL).catch(() => null),
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
          faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
        ]);
        setModelsLoaded(true);
        console.log("Face models loaded for registration");
      } catch (error) {
        console.error("Model load error:", error);
      }
    };
    loadModels();
  }, []);

  useEffect(() => {
    fetchEmployees();
    fetchLeaveRequests();
    fetchEmploymentTypes();
  }, []);

  // 撮り直し時にビデオストリームを再接続
  useEffect(() => {
    if (showPhotoDialog && !capturedPhoto && photoStream && photoVideoRef.current) {
      photoVideoRef.current.srcObject = photoStream;
    }
  }, [showPhotoDialog, capturedPhoto, photoStream]);

  // QRコード印刷
  const printQrCode = async (employee) => {
    const companyCode = localStorage.getItem('company_code') || 'PRC001';
    const qrData = `PRC:${employee.id}:${companyCode}`;
    
    // 顔写真を取得（profile_photoを優先、なければface_encodingsから）
    const rawFacePhoto = employee.profile_photo || employee.face_encodings?.[0]?.image || null;
    const facePhoto = rawFacePhoto 
      ? (rawFacePhoto.startsWith('data:') ? rawFacePhoto : `data:image/jpeg;base64,${rawFacePhoto}`)
      : null;
    
    // QRコードをSVGとして生成
    const QRCode = await import('qrcode');
    
    QRCode.toString(qrData, { type: 'svg', width: 200, margin: 1 }, (err, svg) => {
      if (err) {
        toast.error('QRコード生成に失敗しました');
        return;
      }
      
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>QRコード - ${employee.name}</title>
          <style>
            body { font-family: sans-serif; text-align: center; padding: 20px; }
            .qr-card { border: 2px solid #333; padding: 20px; display: inline-block; margin: 10px; }
            .photo-container { margin-bottom: 15px; }
            .photo { width: 120px; height: 120px; border-radius: 12px; object-fit: cover; border: 3px solid #333; }
            .no-photo { width: 120px; height: 120px; border-radius: 12px; background: #e0e0e0; display: flex; align-items: center; justify-content: center; margin: 0 auto; font-size: 48px; color: #999; border: 3px solid #333; }
            .employee-name { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
            .employee-code { font-size: 16px; color: #666; margin-bottom: 15px; }
            .qr-container { margin: 20px 0; }
            .qr-container svg { width: 200px; height: 200px; }
            .instructions { font-size: 12px; color: #888; margin-top: 10px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="qr-card">
            <div class="photo-container">
              ${facePhoto 
                ? `<img src="${facePhoto}" class="photo" alt="${employee.name}" />`
                : `<div class="no-photo">${employee.name?.charAt(0) || '?'}</div>`
              }
            </div>
            <div class="employee-name">${employee.name}</div>
            <div class="employee-code">${employee.employee_code}</div>
            <div class="qr-container">${svg}</div>
            <div class="instructions">このQRコードをスキャンして打刻</div>
          </div>
          <script>
            setTimeout(function() { window.print(); }, 300);
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
    });
  };

  // 一括QRコード印刷
  const printBulkQrCodes = async () => {
    const companyCode = localStorage.getItem('company_code') || 'PRC001';
    const activeEmployees = employees.filter(e => e.is_active !== false);
    
    // QRコードライブラリをインポート
    const QRCode = await import('qrcode');
    
    // 全従業員のQRコードを生成
    const qrPromises = activeEmployees.map(emp => {
      const qrData = `PRC:${emp.id}:${companyCode}`;
      return new Promise((resolve) => {
        QRCode.toString(qrData, { type: 'svg', width: 120, margin: 1 }, (err, svg) => {
          resolve({ emp, svg: err ? '' : svg });
        });
      });
    });
    
    const qrResults = await Promise.all(qrPromises);
    
    let cardsHtml = qrResults.map(({ emp, svg }) => {
      const rawFacePhoto = emp.profile_photo || emp.face_encodings?.[0]?.image || null;
      const facePhoto = rawFacePhoto 
        ? (rawFacePhoto.startsWith('data:') ? rawFacePhoto : `data:image/jpeg;base64,${rawFacePhoto}`)
        : null;
      return `
        <div class="qr-card">
          <div class="photo-container">
            ${facePhoto 
              ? `<img src="${facePhoto}" class="photo" alt="${emp.name}" />`
              : `<div class="no-photo">${emp.name?.charAt(0) || '?'}</div>`
            }
          </div>
          <div class="employee-name">${emp.name}</div>
          <div class="employee-code">${emp.employee_code}</div>
          <div class="qr-container">${svg}</div>
        </div>
      `;
    }).join('');
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>QRコード一覧</title>
        <style>
          body { font-family: sans-serif; padding: 10px; }
          h2 { text-align: center; }
          .container { display: flex; flex-wrap: wrap; justify-content: center; }
          .qr-card { border: 1px solid #333; padding: 10px; margin: 5px; width: 160px; text-align: center; page-break-inside: avoid; }
          .photo-container { margin-bottom: 8px; }
          .photo { width: 60px; height: 60px; border-radius: 8px; object-fit: cover; border: 2px solid #333; }
          .no-photo { width: 60px; height: 60px; border-radius: 8px; background: #e0e0e0; display: flex; align-items: center; justify-content: center; margin: 0 auto; font-size: 24px; color: #999; border: 2px solid #333; }
          .employee-name { font-size: 12px; font-weight: bold; margin-bottom: 3px; word-break: break-all; }
          .employee-code { font-size: 10px; color: #666; margin-bottom: 5px; }
          .qr-container { margin: 5px 0; }
          .qr-container svg { width: 120px; height: 120px; }
          @media print { 
            .qr-card { border: 1px solid #000; }
          }
        </style>
      </head>
      <body>
        <h2>従業員QRコード一覧（${activeEmployees.length}名）</h2>
        <div class="container">
          ${cardsHtml}
        </div>
        <script>
          setTimeout(function() { window.print(); }, 500);
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
    setShowBulkQrDialog(false);
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/employees`);
      setEmployees(res.data);
    } catch (error) {
      toast.error("従業員情報の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveRequests = async () => {
    setLoadingRequests(true);
    try {
      const res = await axios.get(`${API}/leave/requests`);
      setLeaveRequests(res.data || []);
    } catch (error) {
      console.error("Leave requests fetch error:", error);
      setLeaveRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  };

  const fetchEmploymentTypes = async () => {
    try {
      const res = await axios.get(`${API}/masters/employment-types`);
      setEmploymentTypes(res.data || []);
    } catch (error) {
      console.error("Employment types fetch error:", error);
      // デフォルト値を設定
      setEmploymentTypes([
        { code: "regular", name: "正社員" },
        { code: "contract", name: "契約社員" },
        { code: "part_time", name: "パート" },
        { code: "temporary", name: "アルバイト" },
        { code: "dispatch", name: "派遣社員" }
      ]);
    }
  };

  const createLeaveRequest = async () => {
    if (!newLeaveRequest.employee_id || !newLeaveRequest.start_date || !newLeaveRequest.end_date) {
      toast.error("必須項目を入力してください");
      return;
    }
    setSubmittingRequest(true);
    try {
      await axios.post(`${API}/leave/requests`, newLeaveRequest);
      toast.success("有給申請を作成しました");
      setShowNewRequestDialog(false);
      setNewLeaveRequest({
        employee_id: "",
        leave_type: "paid_leave",
        start_date: "",
        end_date: "",
        days: 1,
        reason: ""
      });
      fetchLeaveRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "申請の作成に失敗しました");
    } finally {
      setSubmittingRequest(false);
    }
  };

  // 雇用形態コードから名前を取得
  const getEmploymentTypeName = (code) => {
    const et = employmentTypes.find(t => t.code === code);
    return et ? et.name : code;
  };

  const handleLeaveRequestAction = async (requestId, action) => {
    setProcessingRequest(requestId);
    try {
      await axios.put(`${API}/leave/requests/${requestId}`, { status: action });
      toast.success(action === "approved" ? "申請を承認しました" : "申請を却下しました");
      fetchLeaveRequests();
      fetchEmployees(); // Update employee leave balances
    } catch (error) {
      toast.error(error.response?.data?.detail || "処理に失敗しました");
    } finally {
      setProcessingRequest(null);
    }
  };

  const getLeaveTypeLabel = (type) => {
    const types = {
      paid_leave: "有給休暇",
      sick_leave: "病気休暇",
      special_leave: "特別休暇"
    };
    return types[type] || type;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-yellow-600">申請中</Badge>;
      case "approved":
        return <Badge className="bg-emerald-600">承認済</Badge>;
      case "rejected":
        return <Badge className="bg-red-600">却下</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // GDPR Data Export for specific employee
  const exportEmployeeData = async (employee) => {
    try {
      const res = await axios.get(`${API}/gdpr/export/employee/${employee.id}`);
      const dataStr = JSON.stringify(res.data, null, 2);
      const blob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `employee_${employee.employee_code}_export_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${employee.name}のデータをエクスポートしました`);
    } catch (error) {
      toast.error("エクスポートに失敗しました");
    }
  };

  // GDPR Anonymize employee
  const anonymizeEmployee = async (employee) => {
    if (!window.confirm(`${employee.name}のデータを匿名化しますか？\n\n匿名化すると個人情報は削除されますが、勤怠統計データは保持されます。この操作は元に戻せません。`)) {
      return;
    }
    try {
      await axios.post(`${API}/gdpr/anonymize/employee/${employee.id}`);
      toast.success("データを匿名化しました");
      fetchEmployees();
    } catch (error) {
      toast.error("匿名化に失敗しました");
    }
  };

  // GDPR Complete deletion
  const deleteEmployeeCompletely = async (employee) => {
    if (!window.confirm(`${employee.name}の全データを完全に削除しますか？\n\n削除されるデータ:\n・従業員情報\n・勤怠記録\n・有給申請履歴\n\nこの操作は元に戻せません。`)) {
      return;
    }
    try {
      await axios.delete(`${API}/gdpr/delete/employee/${employee.id}`);
      toast.success("データを完全に削除しました");
      fetchEmployees();
    } catch (error) {
      toast.error("削除に失敗しました");
    }
  };

  const fetchEmployeeFaces = async (employeeId) => {
    try {
      const res = await axios.get(`${API}/employees/${employeeId}/faces`);
      setRegisteredFaces(res.data);
    } catch (error) {
      setRegisteredFaces([]);
    }
  };

  // Paid Leave Management
  const openPaidLeaveDialog = async (employee) => {
    setSelectedEmployee(employee);
    try {
      const res = await axios.get(`${API}/leave/balance/${employee.id}`);
      setPaidLeaveData({
        granted: res.data.paid_leave_granted || 0,
        used: res.data.paid_leave_used || 0,
        balance: res.data.paid_leave_balance || 0
      });
    } catch (error) {
      setPaidLeaveData({ granted: 0, used: 0, balance: 0 });
    }
    setShowPaidLeaveDialog(true);
  };

  const savePaidLeave = async () => {
    if (!selectedEmployee) return;
    setSavingPaidLeave(true);
    try {
      await axios.put(`${API}/leave/balance/${selectedEmployee.id}`, {
        paid_leave_granted: paidLeaveData.granted,
        paid_leave_balance: paidLeaveData.balance
      });
      toast.success("有給情報を更新しました");
      fetchEmployees();
      setShowPaidLeaveDialog(false);
    } catch (error) {
      toast.error("保存に失敗しました");
    } finally {
      setSavingPaidLeave(false);
    }
  };

  // ID Document Management
  const openIdDocDialog = async (employee) => {
    setSelectedEmployee(employee);
    try {
      const res = await axios.get(`${API}/employees/${employee.id}/documents`);
      setIdDocuments(res.data.id_documents || []);
      setCompanyName(res.data.company_name || "");
    } catch (error) {
      setIdDocuments([]);
      setCompanyName("");
    }
    setNewIdDoc({ type: "在留カード", number: "", expiry: "", image_url: "" });
    setShowIdDocDialog(true);
  };

  const addIdDocument = async () => {
    if (!selectedEmployee || !newIdDoc.number) {
      toast.error("証明書番号を入力してください");
      return;
    }
    setSavingIdDoc(true);
    try {
      await axios.post(`${API}/employees/${selectedEmployee.id}/documents`, {
        type: newIdDoc.type,
        number: newIdDoc.number,
        expiry_date: newIdDoc.expiry || null
      });
      toast.success("身分証明書を追加しました");
      // Refresh documents
      const res = await axios.get(`${API}/employees/${selectedEmployee.id}/documents`);
      setIdDocuments(res.data.id_documents || []);
      setNewIdDoc({ type: "在留カード", number: "", expiry: "", image_url: "" });
    } catch (error) {
      toast.error("追加に失敗しました");
    } finally {
      setSavingIdDoc(false);
    }
  };

  const deleteIdDocument = async (docId) => {
    if (!selectedEmployee) return;
    if (!window.confirm("この身分証明書を削除しますか？")) return;
    try {
      await axios.delete(`${API}/employees/${selectedEmployee.id}/documents/${docId}`);
      toast.success("削除しました");
      setIdDocuments(idDocuments.filter(d => d.id !== docId));
    } catch (error) {
      toast.error("削除に失敗しました");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.id}`, formData);
        toast.success("従業員情報を更新しました");
      } else {
        await axios.post(`${API}/employees`, formData);
        toast.success("従業員を追加しました");
      }
      setShowAddDialog(false);
      setEditingEmployee(null);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "エラーが発生しました");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("この従業員を削除しますか？")) return;
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast.success("従業員を削除しました");
      fetchEmployees();
    } catch (error) {
      toast.error("削除に失敗しました");
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      employee_code: employee.employee_code,
      name: employee.name,
      name_kana: employee.name_kana || "",
      language: employee.language,
      department: employee.department || "",
      position: employee.position || "",
      employment_type: employee.employment_type || "regular",
      email: employee.email || "",
      phone: employee.phone || "",
      whatsapp_number: employee.whatsapp_number || "",
      address: employee.address || "",
      emergency_contact_name: employee.emergency_contact_name || "",
      emergency_contact_phone: employee.emergency_contact_phone || "",
      emergency_contact_relationship: employee.emergency_contact_relationship || "",
      birth_date: employee.birth_date || "",
      hire_date: employee.hire_date || "",
      termination_date: employee.termination_date || "",
      my_number: employee.my_number || "",
      pension_number: employee.pension_number || "",
      health_insurance_number: employee.health_insurance_number || "",
      employment_insurance_number: employee.employment_insurance_number || "",
      nationality: employee.nationality || "",
      is_foreign_worker: employee.is_foreign_worker || false,
      residence_status: employee.residence_status || "",
      residence_period_end: employee.residence_period_end || "",
      residence_card_number: employee.residence_card_number || "",
      passport_number: employee.passport_number || "",
      passport_expiry: employee.passport_expiry || "",
      work_permission: employee.work_permission || "",
      designated_activities: employee.designated_activities || "",
      paid_leave_balance: employee.paid_leave_balance,
      profile_photo: employee.profile_photo || "",
      id_photo_front: employee.id_photo_front || "",
      id_photo_back: employee.id_photo_back || "",
      drivers_license_front: employee.drivers_license_front || "",
      drivers_license_back: employee.drivers_license_back || "",
      residence_card_front: employee.residence_card_front || "",
      residence_card_back: employee.residence_card_back || "",
      resume_page1: employee.resume_page1 || "",
      resume_page2: employee.resume_page2 || ""
    });
    setShowMyNumber(false); // マイナンバーを非表示でリセット
    setShowAddDialog(true);
  };

  const resetForm = () => {
    setFormData({
      employee_code: "",
      name: "",
      name_kana: "",
      language: "ja",
      department: "",
      position: "",
      employment_type: "regular",
      email: "",
      phone: "",
      address: "",
      emergency_contact_name: "",
      emergency_contact_phone: "",
      emergency_contact_relationship: "",
      birth_date: "",
      hire_date: "",
      termination_date: "",
      my_number: "",
      pension_number: "",
      health_insurance_number: "",
      employment_insurance_number: "",
      nationality: "",
      is_foreign_worker: false,
      residence_status: "",
      residence_period_end: "",
      residence_card_number: "",
      passport_number: "",
      passport_expiry: "",
      work_permission: "",
      designated_activities: "",
      paid_leave_balance: 10,
      profile_photo: "",
      id_photo_front: "",
      id_photo_back: "",
      drivers_license_front: "",
      drivers_license_back: "",
      residence_card_front: "",
      residence_card_back: "",
      resume_page1: "",
      resume_page2: ""
    });
  };

  // 写真撮影ダイアログを開く
  const openPhotoDialog = async (type) => {
    setPhotoType(type);
    setCapturedPhoto(null);
    setShowPhotoDialog(true);
    
    // 証明写真の場合は縦長（3:4）、それ以外は横長（4:3）
    const isProfile = type === 'profile';
    const videoWidth = isProfile ? 480 : 640;
    const videoHeight = isProfile ? 640 : 480;
    
    // カメラを起動
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: videoWidth, height: videoHeight }
      });
      setPhotoStream(stream);
      if (photoVideoRef.current) {
        photoVideoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Camera error:", err);
      toast.error("カメラにアクセスできません");
    }
  };

  // 写真撮影ダイアログを閉じる
  const closePhotoDialog = () => {
    if (photoStream) {
      photoStream.getTracks().forEach(track => track.stop());
      setPhotoStream(null);
    }
    setShowPhotoDialog(false);
    setPhotoType(null);
    setCapturedPhoto(null);
  };

  // 撮り直す - カメラを再起動
  const retakePhoto = async () => {
    // まず古いストリームを停止
    if (photoStream) {
      photoStream.getTracks().forEach(track => track.stop());
      setPhotoStream(null);
    }
    
    setCapturedPhoto(null);
    
    // 少し待ってから新しいカメラストリームを取得
    setTimeout(async () => {
      const isProfile = photoType === 'profile';
      const videoWidth = isProfile ? 480 : 640;
      const videoHeight = isProfile ? 640 : 480;
      
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: videoWidth, height: videoHeight }
        });
        setPhotoStream(stream);
        if (photoVideoRef.current) {
          photoVideoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error("Camera error:", err);
        toast.error("カメラにアクセスできません");
      }
    }, 100);
  };

  // カメラで撮影
  const capturePhoto = () => {
    if (!photoVideoRef.current || !photoCanvasRef.current) return;
    
    const video = photoVideoRef.current;
    const canvas = photoCanvasRef.current;
    
    // 証明写真の場合は縦長（3:4）、それ以外は横長（4:3）
    const isProfile = photoType === 'profile';
    const canvasWidth = isProfile ? 300 : 640;
    const canvasHeight = isProfile ? 400 : 480;
    
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext("2d");
    
    // ビデオの実際のサイズを取得
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    
    // 中央からクロップして描画
    const sourceAspect = videoWidth / videoHeight;
    const targetAspect = canvasWidth / canvasHeight;
    
    let sx, sy, sw, sh;
    if (sourceAspect > targetAspect) {
      // ソースが横長すぎる場合、左右をクロップ
      sh = videoHeight;
      sw = videoHeight * targetAspect;
      sx = (videoWidth - sw) / 2;
      sy = 0;
    } else {
      // ソースが縦長すぎる場合、上下をクロップ
      sw = videoWidth;
      sh = videoWidth / targetAspect;
      sx = 0;
      sy = (videoHeight - sh) / 2;
    }
    
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvasWidth, canvasHeight);
    const imageData = canvas.toDataURL("image/jpeg", 0.8);
    setCapturedPhoto(imageData);
  };

  // 撮影した写真を保存
  const savePhoto = () => {
    if (!capturedPhoto || !photoType) return;
    
    const fieldMap = {
      'profile': 'profile_photo',
      'id_front': 'id_photo_front',
      'id_back': 'id_photo_back',
      'license_front': 'drivers_license_front',
      'license_back': 'drivers_license_back',
      'residence_front': 'residence_card_front',
      'residence_back': 'residence_card_back',
      'resume_1': 'resume_page1',
      'resume_2': 'resume_page2'
    };
    
    const fieldName = fieldMap[photoType];
    if (fieldName) {
      setFormData(prev => ({ ...prev, [fieldName]: capturedPhoto }));
      toast.success("写真を設定しました");
    }
    closePhotoDialog();
  };

  // 画像を2MB以下に圧縮する関数
  const compressImage = (file, maxSizeMB = 2) => {
    return new Promise((resolve) => {
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      
      // 既に2MB以下の場合はそのまま読み込み
      if (file.size <= maxSizeBytes) {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(file);
        return;
      }
      
      const img = new window.Image();
      const reader = new FileReader();
      
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // 画像サイズを計算（最大幅/高さ: 1920px）
        let width = img.width;
        let height = img.height;
        const maxDimension = 1920;
        
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        // 品質を調整して2MB以下になるまで圧縮
        let quality = 0.9;
        let result = canvas.toDataURL('image/jpeg', quality);
        
        while (result.length > maxSizeBytes * 1.37 && quality > 0.1) {
          quality -= 0.1;
          result = canvas.toDataURL('image/jpeg', quality);
        }
        
        resolve(result);
      };
      
      reader.readAsDataURL(file);
    });
  };

  // ファイルアップロードで写真を設定
  const handlePhotoUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      // 画像を2MB以下に圧縮
      const compressedImage = await compressImage(file, 2);
      
      const fieldMap = {
        'profile': 'profile_photo',
        'id_front': 'id_photo_front',
        'id_back': 'id_photo_back',
        'license_front': 'drivers_license_front',
        'license_back': 'drivers_license_back',
        'residence_front': 'residence_card_front',
        'residence_back': 'residence_card_back',
        'resume_1': 'resume_page1',
        'resume_2': 'resume_page2'
      };
      const fieldName = fieldMap[type];
      if (fieldName) {
        setFormData(prev => ({ ...prev, [fieldName]: compressedImage }));
        
        // 圧縮された場合はメッセージを表示
        const originalSizeMB = (file.size / 1024 / 1024).toFixed(2);
        const compressedSizeKB = Math.round(compressedImage.length * 0.75 / 1024);
        if (file.size > 2 * 1024 * 1024) {
          toast.success(`写真を圧縮しました (${originalSizeMB}MB → ${compressedSizeKB}KB)`);
        } else {
          toast.success("写真をアップロードしました");
        }
      }
    } catch (error) {
      toast.error("画像の処理に失敗しました");
    }
    e.target.value = "";
  };

  // 写真を削除
  const removePhoto = (type) => {
    const fieldMap = {
      'profile': 'profile_photo',
      'id_front': 'id_photo_front',
      'id_back': 'id_photo_back',
      'license_front': 'drivers_license_front',
      'license_back': 'drivers_license_back',
      'residence_front': 'residence_card_front',
      'residence_back': 'residence_card_back',
      'resume_1': 'resume_page1',
      'resume_2': 'resume_page2'
    };
    const fieldName = fieldMap[type];
    if (fieldName) {
      setFormData(prev => ({ ...prev, [fieldName]: "" }));
    }
  };

  // 写真タイプのラベル取得
  const getPhotoLabel = (type) => {
    const labels = {
      'profile': '証明写真',
      'id_front': '身分証明書（表面）',
      'id_back': '身分証明書（裏面）',
      'license_front': '運転免許証（表面）',
      'license_back': '運転免許証（裏面）',
      'residence_front': '在留カード（表面）',
      'residence_back': '在留カード（裏面）',
      'resume_1': '履歴書（一面）',
      'resume_2': '履歴書（二面）'
    };
    return labels[type] || type;
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formDataObj = new FormData();
    formDataObj.append("file", file);
    try {
      const res = await axios.post(`${API}/employees/import-csv`, formDataObj, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`${res.data.imported}件の従業員をインポートしました`);
      fetchEmployees();
    } catch (error) {
      toast.error("CSVインポートに失敗しました");
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // 顔枠描画
  const drawFaceBox = (detection) => {
    const canvas = overlayCanvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;

    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (detection) {
      const box = detection.detection.box;
      const x = canvas.width - box.x - box.width;
      const y = box.y;
      const width = box.width;
      const height = box.height;

      // 緑の枠
      ctx.strokeStyle = '#10b981';
      ctx.lineWidth = 3;
      ctx.strokeRect(x, y, width, height);

      // コーナーマーク
      const cornerLength = 20;
      ctx.lineWidth = 4;
      
      ctx.beginPath();
      ctx.moveTo(x, y + cornerLength);
      ctx.lineTo(x, y);
      ctx.lineTo(x + cornerLength, y);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(x + width - cornerLength, y);
      ctx.lineTo(x + width, y);
      ctx.lineTo(x + width, y + cornerLength);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(x, y + height - cornerLength);
      ctx.lineTo(x, y + height);
      ctx.lineTo(x + cornerLength, y + height);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(x + width - cornerLength, y + height);
      ctx.lineTo(x + width, y + height);
      ctx.lineTo(x + width, y + height - cornerLength);
      ctx.stroke();
    }
  };

  // 顔検出ループ
  const startFaceDetection = () => {
    if (!modelsLoaded) return;

    const detect = async () => {
      if (!videoRef.current || videoRef.current.readyState < 2) {
        animationRef.current = requestAnimationFrame(detect);
        return;
      }

      try {
        const detection = await faceapi
          .detectSingleFace(videoRef.current, DETECTION_OPTIONS)
          .withFaceLandmarks();

        if (detection) {
          setFaceDetected(true);
          drawFaceBox(detection);
        } else {
          setFaceDetected(false);
          if (overlayCanvasRef.current) {
            const ctx = overlayCanvasRef.current.getContext('2d');
            ctx.clearRect(0, 0, overlayCanvasRef.current.width, overlayCanvasRef.current.height);
          }
        }
      } catch (e) {
        console.error(e);
      }

      animationRef.current = requestAnimationFrame(detect);
    };

    detect();
  };

  const startFaceCapture = async (employee) => {
    setSelectedEmployee(employee);
    setShowFaceDialog(true);
    setFaceLabel("");
    setFaceDetected(false);
    await fetchEmployeeFaces(employee.id);

    // カメラデバイス一覧を取得
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      setAvailableCameras(videoDevices);
    } catch (e) {
      console.error("Camera enumeration error:", e);
    }

    setTimeout(() => startCamera(cameraFacing), 100);
  };

  const startCamera = async (facing) => {
    // 既存のストリームを停止
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: 640, height: 480 }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          startFaceDetection();
        };
      }
    } catch (err) {
      toast.error("カメラにアクセスできません");
    }
  };

  const switchCamera = (facing) => {
    setCameraFacing(facing);
    startCamera(facing);
  };

  const captureFace = async () => {
    if (!videoRef.current || !canvasRef.current || !selectedEmployee || !faceDetected) {
      if (!faceDetected) toast.error("顔を検出してから撮影してください");
      return;
    }

    setCapturing(true);
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = 320;
    canvas.height = 240;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, 320, 240);
    const imageData = canvas.toDataURL("image/jpeg", 0.4);
    const base64 = imageData.split(",")[1];

    try {
      // 特徴量（descriptor）を計算
      let descriptor = null;
      try {
        const detection = await faceapi
          .detectSingleFace(canvas, SSD_OPTIONS)
          .withFaceLandmarks()
          .withFaceDescriptor();
        if (detection?.descriptor) {
          descriptor = Array.from(detection.descriptor);
          console.log("Descriptor computed for registration");
        }
      } catch (descError) {
        console.log("Descriptor computation failed, registering without:", descError);
      }

      const res = await axios.post(`${API}/face-register`, {
        employee_id: selectedEmployee.id,
        image_base64: base64,
        label: faceLabel || null,
        descriptor: descriptor
      });
      toast.success(res.data.message);
      setFaceLabel("");
      await fetchEmployeeFaces(selectedEmployee.id);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "顔の登録に失敗しました");
    } finally {
      setCapturing(false);
    }
  };

  const deleteFace = async (faceId) => {
    if (!selectedEmployee) return;
    if (!window.confirm("この顔データを削除しますか？")) return;
    try {
      await axios.delete(`${API}/face-register/${selectedEmployee.id}/${faceId}`);
      toast.success("顔データを削除しました");
      await fetchEmployeeFaces(selectedEmployee.id);
      fetchEmployees();
    } catch (error) {
      toast.error("削除に失敗しました");
    }
  };

  const closeFaceDialog = () => {
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setShowFaceDialog(false);
    setSelectedEmployee(null);
    setRegisteredFaces([]);
    setFaceDetected(false);
  };

  const getFaceCount = (employee) => employee.face_encodings?.length || 0;

  const filteredEmployees = employees.filter(emp =>
    emp.name.toLowerCase().includes(search.toLowerCase()) ||
    emp.employee_code.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold mb-1">従業員管理</h1>
          <p className="text-slate-400 text-sm">従業員の追加・編集・有給申請</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-800 border-slate-700">
          <TabsTrigger value="employees" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <Users className="w-4 h-4 mr-2" />
            従業員一覧
          </TabsTrigger>
          <TabsTrigger value="leave-requests" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <ClipboardList className="w-4 h-4 mr-2" />
            有給申請
            {leaveRequests.filter(r => r.status === "pending").length > 0 && (
              <Badge className="ml-2 bg-yellow-600">{leaveRequests.filter(r => r.status === "pending").length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Employees Tab */}
        <TabsContent value="employees" className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="検索..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-slate-800 border-slate-700" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowBulkQrDialog(true)} className="border-purple-600 text-purple-400 hover:bg-purple-900/20">
                <QrCode className="w-4 h-4 mr-1" />
                QR一括印刷
              </Button>
              <input type="file" ref={fileInputRef} onChange={handleCSVImport} accept=".csv" className="hidden" />
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} className="border-slate-600">
                <Upload className="w-4 h-4 mr-1" />
                CSV
              </Button>
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" onClick={() => { setEditingEmployee(null); resetForm(); setShowMyNumber(false); }}>
                    <Plus className="w-4 h-4 mr-1" />
                    追加
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700 text-white">
                  <DialogHeader>
                    <DialogTitle>{editingEmployee ? "従業員編集" : "従業員追加"}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Profile Photo Section */}
                    <div className="p-3 bg-slate-700/50 rounded-lg space-y-3">
                      <h4 className="font-medium text-emerald-400 text-sm flex items-center gap-2">
                        <Camera className="w-4 h-4" />
                        証明写真
                      </h4>
                      <div className="flex items-center gap-3">
                        {formData.profile_photo ? (
                          <div className="relative w-20 h-24">
                            <img src={formData.profile_photo} alt="証明写真" className="w-20 h-24 rounded-lg object-cover border border-slate-600" />
                            <button type="button" onClick={() => removePhoto('profile')} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs hover:bg-red-600">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="w-20 h-24 bg-slate-600 rounded-lg flex items-center justify-center border border-slate-500">
                            <Users className="w-8 h-8 text-slate-400" />
                          </div>
                        )}
                        <div className="flex flex-col gap-2">
                          <Button type="button" variant="outline" size="sm" className="h-8 text-xs border-slate-600" onClick={() => openPhotoDialog('profile')}>
                            <Camera className="w-3 h-3 mr-1" />カメラで撮影
                          </Button>
                          <label className="cursor-pointer">
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(e, 'profile')} />
                            <span className="inline-flex items-center justify-center h-8 px-3 text-xs border border-slate-600 rounded-md hover:bg-slate-600">
                              <Upload className="w-3 h-3 mr-1" />ファイル選択
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Basic Info */}
                    <div className="p-3 bg-slate-700/50 rounded-lg space-y-3">
                      <h4 className="font-medium text-emerald-400 text-sm">基本情報</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">社員コード *</Label>
                          <Input value={formData.employee_code} onChange={(e) => setFormData({ ...formData, employee_code: e.target.value })} required disabled={!!editingEmployee} className="bg-slate-700 border-slate-600 h-9" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">氏名 *</Label>
                          <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="bg-slate-700 border-slate-600 h-9" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">フリガナ</Label>
                          <Input value={formData.name_kana} onChange={(e) => setFormData({ ...formData, name_kana: e.target.value })} className="bg-slate-700 border-slate-600 h-9" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">生年月日</Label>
                          <Input type="date" value={formData.birth_date} onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })} className="bg-slate-700 border-slate-600 h-9" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">母国語</Label>
                          <Select value={formData.language} onValueChange={(v) => setFormData({ ...formData, language: v })}>
                            <SelectTrigger className="bg-slate-700 border-slate-600 h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {LANGUAGES.map(lang => (<SelectItem key={lang.code} value={lang.code}><span className="inline-flex items-center gap-2"><Flag countryCode={lang.countryCode} size={16} /> {lang.name}</span></SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">国籍</Label>
                          <Input value={formData.nationality} onChange={(e) => setFormData({ ...formData, nationality: e.target.value })} placeholder="日本" className="bg-slate-700 border-slate-600 h-9" />
                        </div>
                      </div>
                    </div>

                    {/* Employment Info */}
                    <div className="p-3 bg-slate-700/50 rounded-lg space-y-3">
                      <h4 className="font-medium text-emerald-400 text-sm">雇用情報</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">部署</Label>
                          <Input value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} className="bg-slate-700 border-slate-600 h-9" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">役職</Label>
                          <Input value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} className="bg-slate-700 border-slate-600 h-9" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">雇用形態</Label>
                          <Select value={formData.employment_type} onValueChange={(v) => setFormData({ ...formData, employment_type: v })}>
                            <SelectTrigger className="bg-slate-700 border-slate-600 h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {employmentTypes.map(et => (
                                <SelectItem key={et.code} value={et.code}>{et.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">有給残日数</Label>
                          <Input type="number" step="0.5" value={formData.paid_leave_balance} onChange={(e) => setFormData({ ...formData, paid_leave_balance: parseFloat(e.target.value) })} className="bg-slate-700 border-slate-600 h-9" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">入社日</Label>
                          <Input type="date" value={formData.hire_date} onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })} className="bg-slate-700 border-slate-600 h-9" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">退職日</Label>
                          <Input type="date" value={formData.termination_date} onChange={(e) => setFormData({ ...formData, termination_date: e.target.value })} className="bg-slate-700 border-slate-600 h-9" />
                        </div>
                        <div className="col-span-2 space-y-1">
                          <Label className="text-xs">マイナンバー（個人番号）</Label>
                          <div className="relative">
                            <Input 
                              type={showMyNumber ? "text" : "password"}
                              value={formData.my_number} 
                              onChange={(e) => setFormData({ ...formData, my_number: e.target.value.replace(/[^0-9]/g, '').slice(0, 12) })} 
                              placeholder="12桁の数字" 
                              maxLength={12}
                              className="bg-slate-700 border-slate-600 h-9 tracking-wider pr-10" 
                              data-testid="my-number-input"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 hover:bg-slate-600"
                              onClick={() => setShowMyNumber(!showMyNumber)}
                              data-testid="toggle-my-number-visibility"
                            >
                              {showMyNumber ? (
                                <EyeOff className="w-4 h-4 text-slate-400" />
                              ) : (
                                <Eye className="w-4 h-4 text-slate-400" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Insurance Numbers */}
                    <div className="p-3 bg-slate-700/50 rounded-lg space-y-3">
                      <h4 className="font-medium text-emerald-400 text-sm">社会保険情報</h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">基礎年金番号</Label>
                          <Input 
                            value={formData.pension_number} 
                            onChange={(e) => setFormData({ ...formData, pension_number: e.target.value })} 
                            placeholder="xxxx-xxxxxx" 
                            className="bg-slate-700 border-slate-600 h-9" 
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">健康保険番号</Label>
                          <Input 
                            value={formData.health_insurance_number} 
                            onChange={(e) => setFormData({ ...formData, health_insurance_number: e.target.value })} 
                            className="bg-slate-700 border-slate-600 h-9" 
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">雇用保険被保険者番号</Label>
                          <Input 
                            value={formData.employment_insurance_number} 
                            onChange={(e) => setFormData({ ...formData, employment_insurance_number: e.target.value })} 
                            placeholder="xxxx-xxxxxx-x" 
                            className="bg-slate-700 border-slate-600 h-9" 
                          />
                        </div>
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div className="p-3 bg-slate-700/50 rounded-lg space-y-3">
                      <h4 className="font-medium text-emerald-400 text-sm">連絡先</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">メールアドレス</Label>
                          <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-slate-700 border-slate-600 h-9" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">電話番号</Label>
                          <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="090-xxxx-xxxx" className="bg-slate-700 border-slate-600 h-9" />
                        </div>
                        <div className="col-span-2 space-y-1">
                          <Label className="text-xs flex items-center gap-1.5 text-emerald-400">
                            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp番号（通知用）
                          </Label>
                          <Input value={formData.whatsapp_number} onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })} placeholder="090-xxxx-xxxx（通知を使わない場合は空欄）" className="bg-slate-700 border-slate-600 h-9" data-testid="employee-whatsapp-number" />
                          <p className="text-[11px] text-slate-400">打刻・勤怠のお知らせをこの番号のWhatsAppへ送信します。電話番号とは別に管理されます。</p>
                        </div>
                        <div className="col-span-2 space-y-1">
                          <Label className="text-xs">住所</Label>
                          <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="bg-slate-700 border-slate-600 h-9" />
                        </div>
                      </div>
                    </div>

                    {/* Emergency Contact */}
                    <div className="p-3 bg-slate-700/50 rounded-lg space-y-3">
                      <h4 className="font-medium text-emerald-400 text-sm">緊急連絡先</h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">氏名</Label>
                          <Input value={formData.emergency_contact_name} onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })} className="bg-slate-700 border-slate-600 h-9" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">電話番号</Label>
                          <Input value={formData.emergency_contact_phone} onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })} className="bg-slate-700 border-slate-600 h-9" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">続柄</Label>
                          <Input value={formData.emergency_contact_relationship} onChange={(e) => setFormData({ ...formData, emergency_contact_relationship: e.target.value })} placeholder="配偶者、父、母等" className="bg-slate-700 border-slate-600 h-9" />
                        </div>
                      </div>
                    </div>

                    {/* Foreign Worker Info */}
                    <div className="p-3 bg-slate-700/50 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-emerald-400 text-sm">外国人雇用情報</h4>
                        <div className="flex items-center gap-2">
                          <Switch 
                            checked={formData.is_foreign_worker} 
                            onCheckedChange={(v) => setFormData({ ...formData, is_foreign_worker: v })}
                          />
                          <Label className="text-xs">外国人労働者</Label>
                        </div>
                      </div>
                      {formData.is_foreign_worker && (
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">在留資格 *</Label>
                            <Select value={formData.residence_status} onValueChange={(v) => setFormData({ ...formData, residence_status: v })}>
                              <SelectTrigger className="bg-slate-700 border-slate-600 h-9"><SelectValue placeholder="選択" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="engineer">技術・人文知識・国際業務</SelectItem>
                                <SelectItem value="skilled_labor">技能</SelectItem>
                                <SelectItem value="specified_skilled_1">特定技能1号</SelectItem>
                                <SelectItem value="specified_skilled_2">特定技能2号</SelectItem>
                                <SelectItem value="technical_intern_1">技能実習1号</SelectItem>
                                <SelectItem value="technical_intern_2">技能実習2号</SelectItem>
                                <SelectItem value="technical_intern_3">技能実習3号</SelectItem>
                                <SelectItem value="student">留学（資格外活動）</SelectItem>
                                <SelectItem value="dependent">家族滞在</SelectItem>
                                <SelectItem value="permanent">永住者</SelectItem>
                                <SelectItem value="spouse_of_japanese">日本人の配偶者等</SelectItem>
                                <SelectItem value="long_term">定住者</SelectItem>
                                <SelectItem value="designated">特定活動</SelectItem>
                                <SelectItem value="other">その他</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">在留期間満了日 *</Label>
                            <Input type="date" value={formData.residence_period_end} onChange={(e) => setFormData({ ...formData, residence_period_end: e.target.value })} className="bg-slate-700 border-slate-600 h-9" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">在留カード番号 *</Label>
                            <Input value={formData.residence_card_number} onChange={(e) => setFormData({ ...formData, residence_card_number: e.target.value })} placeholder="例: AB12345678CD" className="bg-slate-700 border-slate-600 h-9" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">資格外活動許可</Label>
                            <Select value={formData.work_permission} onValueChange={(v) => setFormData({ ...formData, work_permission: v })}>
                              <SelectTrigger className="bg-slate-700 border-slate-600 h-9"><SelectValue placeholder="選択" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="not_required">不要（就労制限なし）</SelectItem>
                                <SelectItem value="permitted">許可あり</SelectItem>
                                <SelectItem value="none">なし</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">パスポート番号</Label>
                            <Input value={formData.passport_number} onChange={(e) => setFormData({ ...formData, passport_number: e.target.value })} className="bg-slate-700 border-slate-600 h-9" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">パスポート有効期限</Label>
                            <Input type="date" value={formData.passport_expiry} onChange={(e) => setFormData({ ...formData, passport_expiry: e.target.value })} className="bg-slate-700 border-slate-600 h-9" />
                          </div>
                          {formData.residence_status === "designated" && (
                            <div className="col-span-2 space-y-1">
                              <Label className="text-xs">特定活動の内容</Label>
                              <Input value={formData.designated_activities} onChange={(e) => setFormData({ ...formData, designated_activities: e.target.value })} className="bg-slate-700 border-slate-600 h-9" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Documents & Certificates Section */}
                    <div className="p-3 bg-slate-700/50 rounded-lg space-y-4">
                      <h4 className="font-medium text-emerald-400 text-sm flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        書類・証明書
                      </h4>
                      
                      {/* 身分証明書 */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-slate-300">身分証明書</Label>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-400">表面</Label>
                            <div className="flex items-center gap-2">
                              {formData.id_photo_front ? (
                                <div className="relative w-24 h-16">
                                  <img src={formData.id_photo_front} alt="身分証明書表面" className="w-24 h-16 rounded object-cover border border-slate-600" />
                                  <button type="button" onClick={() => removePhoto('id_front')} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600">
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <div className="w-24 h-16 bg-slate-600 rounded flex items-center justify-center border border-dashed border-slate-500">
                                  <CreditCard className="w-5 h-5 text-slate-400" />
                                </div>
                              )}
                              <div className="flex flex-col gap-1">
                                <Button type="button" variant="outline" size="sm" className="h-6 text-xs border-slate-600 px-2" onClick={() => openPhotoDialog('id_front')}>
                                  <Camera className="w-3 h-3 mr-1" />撮影
                                </Button>
                                <label className="cursor-pointer">
                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(e, 'id_front')} />
                                  <span className="inline-flex items-center justify-center h-6 px-2 text-xs border border-slate-600 rounded-md hover:bg-slate-600">
                                    <Upload className="w-3 h-3 mr-1" />選択
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-400">裏面</Label>
                            <div className="flex items-center gap-2">
                              {formData.id_photo_back ? (
                                <div className="relative w-24 h-16">
                                  <img src={formData.id_photo_back} alt="身分証明書裏面" className="w-24 h-16 rounded object-cover border border-slate-600" />
                                  <button type="button" onClick={() => removePhoto('id_back')} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600">
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <div className="w-24 h-16 bg-slate-600 rounded flex items-center justify-center border border-dashed border-slate-500">
                                  <CreditCard className="w-5 h-5 text-slate-400" />
                                </div>
                              )}
                              <div className="flex flex-col gap-1">
                                <Button type="button" variant="outline" size="sm" className="h-6 text-xs border-slate-600 px-2" onClick={() => openPhotoDialog('id_back')}>
                                  <Camera className="w-3 h-3 mr-1" />撮影
                                </Button>
                                <label className="cursor-pointer">
                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(e, 'id_back')} />
                                  <span className="inline-flex items-center justify-center h-6 px-2 text-xs border border-slate-600 rounded-md hover:bg-slate-600">
                                    <Upload className="w-3 h-3 mr-1" />選択
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 運転免許証 */}
                      <div className="space-y-2 pt-2 border-t border-slate-600">
                        <Label className="text-xs font-medium text-slate-300">運転免許証</Label>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-400">表面</Label>
                            <div className="flex items-center gap-2">
                              {formData.drivers_license_front ? (
                                <div className="relative w-24 h-16">
                                  <img src={formData.drivers_license_front} alt="運転免許証表面" className="w-24 h-16 rounded object-cover border border-slate-600" />
                                  <button type="button" onClick={() => removePhoto('license_front')} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600">
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <div className="w-24 h-16 bg-slate-600 rounded flex items-center justify-center border border-dashed border-slate-500">
                                  <CreditCard className="w-5 h-5 text-slate-400" />
                                </div>
                              )}
                              <div className="flex flex-col gap-1">
                                <Button type="button" variant="outline" size="sm" className="h-6 text-xs border-slate-600 px-2" onClick={() => openPhotoDialog('license_front')}>
                                  <Camera className="w-3 h-3 mr-1" />撮影
                                </Button>
                                <label className="cursor-pointer">
                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(e, 'license_front')} />
                                  <span className="inline-flex items-center justify-center h-6 px-2 text-xs border border-slate-600 rounded-md hover:bg-slate-600">
                                    <Upload className="w-3 h-3 mr-1" />選択
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-400">裏面</Label>
                            <div className="flex items-center gap-2">
                              {formData.drivers_license_back ? (
                                <div className="relative w-24 h-16">
                                  <img src={formData.drivers_license_back} alt="運転免許証裏面" className="w-24 h-16 rounded object-cover border border-slate-600" />
                                  <button type="button" onClick={() => removePhoto('license_back')} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600">
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <div className="w-24 h-16 bg-slate-600 rounded flex items-center justify-center border border-dashed border-slate-500">
                                  <CreditCard className="w-5 h-5 text-slate-400" />
                                </div>
                              )}
                              <div className="flex flex-col gap-1">
                                <Button type="button" variant="outline" size="sm" className="h-6 text-xs border-slate-600 px-2" onClick={() => openPhotoDialog('license_back')}>
                                  <Camera className="w-3 h-3 mr-1" />撮影
                                </Button>
                                <label className="cursor-pointer">
                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(e, 'license_back')} />
                                  <span className="inline-flex items-center justify-center h-6 px-2 text-xs border border-slate-600 rounded-md hover:bg-slate-600">
                                    <Upload className="w-3 h-3 mr-1" />選択
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 在留カード（外国人の場合） */}
                      {formData.is_foreign_worker && (
                        <div className="space-y-2 pt-2 border-t border-slate-600">
                          <Label className="text-xs font-medium text-orange-400">在留カード（外国人従業員）</Label>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-slate-400">表面</Label>
                              <div className="flex items-center gap-2">
                                {formData.residence_card_front ? (
                                  <div className="relative w-24 h-16">
                                    <img src={formData.residence_card_front} alt="在留カード表面" className="w-24 h-16 rounded object-cover border border-slate-600" />
                                    <button type="button" onClick={() => removePhoto('residence_front')} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600">
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="w-24 h-16 bg-slate-600 rounded flex items-center justify-center border border-dashed border-slate-500">
                                    <FileText className="w-5 h-5 text-slate-400" />
                                  </div>
                                )}
                                <div className="flex flex-col gap-1">
                                  <Button type="button" variant="outline" size="sm" className="h-6 text-xs border-slate-600 px-2" onClick={() => openPhotoDialog('residence_front')}>
                                    <Camera className="w-3 h-3 mr-1" />撮影
                                  </Button>
                                  <label className="cursor-pointer">
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(e, 'residence_front')} />
                                    <span className="inline-flex items-center justify-center h-6 px-2 text-xs border border-slate-600 rounded-md hover:bg-slate-600">
                                      <Upload className="w-3 h-3 mr-1" />選択
                                    </span>
                                  </label>
                                </div>
                              </div>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-slate-400">裏面</Label>
                              <div className="flex items-center gap-2">
                                {formData.residence_card_back ? (
                                  <div className="relative w-24 h-16">
                                    <img src={formData.residence_card_back} alt="在留カード裏面" className="w-24 h-16 rounded object-cover border border-slate-600" />
                                    <button type="button" onClick={() => removePhoto('residence_back')} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600">
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="w-24 h-16 bg-slate-600 rounded flex items-center justify-center border border-dashed border-slate-500">
                                    <FileText className="w-5 h-5 text-slate-400" />
                                  </div>
                                )}
                                <div className="flex flex-col gap-1">
                                  <Button type="button" variant="outline" size="sm" className="h-6 text-xs border-slate-600 px-2" onClick={() => openPhotoDialog('residence_back')}>
                                    <Camera className="w-3 h-3 mr-1" />撮影
                                  </Button>
                                  <label className="cursor-pointer">
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(e, 'residence_back')} />
                                    <span className="inline-flex items-center justify-center h-6 px-2 text-xs border border-slate-600 rounded-md hover:bg-slate-600">
                                      <Upload className="w-3 h-3 mr-1" />選択
                                    </span>
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 履歴書 */}
                      <div className="space-y-2 pt-2 border-t border-slate-600">
                        <Label className="text-xs font-medium text-slate-300">履歴書</Label>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-400">一面</Label>
                            <div className="flex items-center gap-2">
                              {formData.resume_page1 ? (
                                <div className="relative w-24 h-16">
                                  <img src={formData.resume_page1} alt="履歴書一面" className="w-24 h-16 rounded object-cover border border-slate-600" />
                                  <button type="button" onClick={() => removePhoto('resume_1')} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600">
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <div className="w-24 h-16 bg-slate-600 rounded flex items-center justify-center border border-dashed border-slate-500">
                                  <FileText className="w-5 h-5 text-slate-400" />
                                </div>
                              )}
                              <div className="flex flex-col gap-1">
                                <Button type="button" variant="outline" size="sm" className="h-6 text-xs border-slate-600 px-2" onClick={() => openPhotoDialog('resume_1')}>
                                  <Camera className="w-3 h-3 mr-1" />撮影
                                </Button>
                                <label className="cursor-pointer">
                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(e, 'resume_1')} />
                                  <span className="inline-flex items-center justify-center h-6 px-2 text-xs border border-slate-600 rounded-md hover:bg-slate-600">
                                    <Upload className="w-3 h-3 mr-1" />選択
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-400">二面</Label>
                            <div className="flex items-center gap-2">
                              {formData.resume_page2 ? (
                                <div className="relative w-24 h-16">
                                  <img src={formData.resume_page2} alt="履歴書二面" className="w-24 h-16 rounded object-cover border border-slate-600" />
                                  <button type="button" onClick={() => removePhoto('resume_2')} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600">
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <div className="w-24 h-16 bg-slate-600 rounded flex items-center justify-center border border-dashed border-slate-500">
                                  <FileText className="w-5 h-5 text-slate-400" />
                                </div>
                              )}
                              <div className="flex flex-col gap-1">
                                <Button type="button" variant="outline" size="sm" className="h-6 text-xs border-slate-600 px-2" onClick={() => openPhotoDialog('resume_2')}>
                                  <Camera className="w-3 h-3 mr-1" />撮影
                                </Button>
                                <label className="cursor-pointer">
                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(e, 'resume_2')} />
                                  <span className="inline-flex items-center justify-center h-6 px-2 text-xs border border-slate-600 rounded-md hover:bg-slate-600">
                                    <Upload className="w-3 h-3 mr-1" />選択
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} className="flex-1 border-slate-600">キャンセル</Button>
                      <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="employee-form-submit-btn">{editingEmployee ? "更新" : "追加"}</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Employee List */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredEmployees.map(employee => {
                const faceCount = getFaceCount(employee);
                const rawFacePhoto = employee.profile_photo || employee.face_encodings?.[0]?.image || null;
                // base64画像にプレフィックスがない場合は付加
                const facePhoto = rawFacePhoto 
                  ? (rawFacePhoto.startsWith('data:') ? rawFacePhoto : `data:image/jpeg;base64,${rawFacePhoto}`)
                  : null;
                return (
                  <Card key={employee.id} className="bg-slate-800 border-slate-700">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {facePhoto ? (
                            <img 
                              src={facePhoto} 
                              alt={employee.name}
                              className="w-10 h-10 rounded-lg object-cover border border-slate-600"
                              style={{ transform: 'scaleX(-1)' }}
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center text-slate-300 font-medium text-lg">
                              {employee.name?.charAt(0) || '?'}
                            </div>
                          )}
                          <div>
                            <h3 className="font-medium text-sm">{employee.name}</h3>
                            <p className="text-xs text-slate-400">{employee.employee_code}</p>
                          </div>
                        </div>
                        {faceCount > 0 ? (
                          <Badge className="bg-emerald-600 text-xs">{faceCount}枚</Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-400 border-slate-600 text-xs">未登録</Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-400 mb-3 space-y-1">
                        <div className="flex items-center gap-2">
                          <Flag countryCode={LANGUAGES.find(l => l.code === employee.language)?.countryCode || 'jp'} size={14} />
                          {LANGUAGES.find(l => l.code === employee.language)?.name || employee.language}
                        </div>
                        {employee.department && <div>部門: {employee.department}</div>}
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          有給残: {employee.paid_leave_balance || 0}日
                        </div>
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        <Button variant="outline" size="sm" className="h-8 text-xs border-slate-600 text-blue-400" onClick={() => openPaidLeaveDialog(employee)}>
                          <Calendar className="w-3 h-3 mr-1" />有給
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 text-xs border-slate-600 text-purple-400" onClick={() => openIdDocDialog(employee)}>
                          <FileText className="w-3 h-3 mr-1" />証明書
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 text-xs border-purple-600 text-purple-400 hover:bg-purple-900/20" onClick={() => printQrCode(employee)} title="QRコード印刷">
                          <QrCode className="w-3 h-3" />
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 text-xs border-slate-600 text-amber-400" onClick={() => navigate(`/admin/employees/${employee.id}/salary`)}>
                          <JapaneseYen className="w-3 h-3 mr-1" />給与
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 border-slate-600" onClick={() => handleEdit(employee)} data-testid={`employee-edit-btn-${employee.employee_code}`}>
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 border-slate-600 text-emerald-400" onClick={() => exportEmployeeData(employee)} title="データエクスポート">
                          <Download className="w-3 h-3" />
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 border-slate-600 text-yellow-400" onClick={() => anonymizeEmployee(employee)} title="匿名化">
                          <UserX className="w-3 h-3" />
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 border-slate-600 text-red-400 hover:text-red-300" onClick={() => deleteEmployeeCompletely(employee)} title="完全削除">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {filteredEmployees.length === 0 && !loading && (
            <Card className="p-8 text-center bg-slate-800 border-slate-700">
              <Users className="w-10 h-10 mx-auto mb-3 text-slate-500" />
              <h3 className="font-medium mb-1">従業員がいません</h3>
              <p className="text-slate-400 text-sm mb-4">従業員を追加してください</p>
              <Button onClick={() => setShowAddDialog(true)}><Plus className="w-4 h-4 mr-1" />追加</Button>
            </Card>
          )}
        </TabsContent>

        {/* Leave Requests Tab */}
        <TabsContent value="leave-requests" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">有給申請一覧</h2>
            <Dialog open={showNewRequestDialog} onOpenChange={setShowNewRequestDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-leave-request-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  新規申請
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white" data-testid="new-leave-request-dialog">
                <DialogHeader>
                  <DialogTitle>新規有給申請</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>従業員 *</Label>
                    <Select 
                      value={newLeaveRequest.employee_id} 
                      onValueChange={(v) => setNewLeaveRequest({ ...newLeaveRequest, employee_id: v })}
                    >
                      <SelectTrigger className="bg-slate-700 border-slate-600" data-testid="leave-employee-select">
                        <SelectValue placeholder="従業員を選択" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.name} ({emp.employee_code}) - 残{emp.paid_leave_balance || 0}日
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>休暇種類</Label>
                    <Select 
                      value={newLeaveRequest.leave_type} 
                      onValueChange={(v) => setNewLeaveRequest({ ...newLeaveRequest, leave_type: v })}
                    >
                      <SelectTrigger className="bg-slate-700 border-slate-600" data-testid="leave-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="paid_leave">有給休暇</SelectItem>
                        <SelectItem value="sick_leave">病気休暇</SelectItem>
                        <SelectItem value="special_leave">特別休暇</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>開始日 *</Label>
                      <Input 
                        type="date" 
                        value={newLeaveRequest.start_date} 
                        onChange={(e) => setNewLeaveRequest({ ...newLeaveRequest, start_date: e.target.value })}
                        className="bg-slate-700 border-slate-600"
                        data-testid="leave-start-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>終了日 *</Label>
                      <Input 
                        type="date" 
                        value={newLeaveRequest.end_date} 
                        onChange={(e) => setNewLeaveRequest({ ...newLeaveRequest, end_date: e.target.value })}
                        className="bg-slate-700 border-slate-600"
                        data-testid="leave-end-date"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>日数</Label>
                    <Input 
                      type="number" 
                      step="0.5"
                      min="0.5"
                      value={newLeaveRequest.days} 
                      onChange={(e) => setNewLeaveRequest({ ...newLeaveRequest, days: parseFloat(e.target.value) || 1 })}
                      className="bg-slate-700 border-slate-600"
                      data-testid="leave-days-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>理由</Label>
                    <Input 
                      value={newLeaveRequest.reason} 
                      onChange={(e) => setNewLeaveRequest({ ...newLeaveRequest, reason: e.target.value })}
                      placeholder="申請理由（任意）"
                      className="bg-slate-700 border-slate-600"
                      data-testid="leave-reason-input"
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowNewRequestDialog(false)} 
                      className="flex-1 border-slate-600"
                    >
                      キャンセル
                    </Button>
                    <Button 
                      onClick={createLeaveRequest} 
                      disabled={submittingRequest}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      data-testid="submit-leave-request-btn"
                    >
                      {submittingRequest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      申請
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {loadingRequests ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : leaveRequests.length === 0 ? (
            <Card className="p-8 text-center bg-slate-800 border-slate-700">
              <ClipboardList className="w-10 h-10 mx-auto mb-3 text-slate-500" />
              <h3 className="font-medium mb-1">有給申請がありません</h3>
              <p className="text-slate-400 text-sm mb-4">新規申請ボタンから申請を作成してください</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {leaveRequests.map(request => (
                <Card key={request.id} className="bg-slate-800 border-slate-700" data-testid={`leave-request-${request.id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-medium">{request.employee_name}</h3>
                          {getStatusBadge(request.status)}
                          <Badge variant="outline" className="border-slate-600">
                            {getLeaveTypeLabel(request.leave_type)}
                          </Badge>
                        </div>
                        <div className="text-sm text-slate-400 space-y-1">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>{request.start_date} 〜 {request.end_date}</span>
                            <span className="text-emerald-400">({request.days}日)</span>
                          </div>
                          {request.reason && (
                            <p className="text-xs">理由: {request.reason}</p>
                          )}
                          <p className="text-xs text-slate-500">
                            申請日: {new Date(request.created_at).toLocaleDateString('ja-JP')}
                          </p>
                        </div>
                      </div>
                      {request.status === "pending" && (
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            className="bg-emerald-600 hover:bg-emerald-700 h-8"
                            onClick={() => handleLeaveRequestAction(request.id, "approved")}
                            disabled={processingRequest === request.id}
                            data-testid={`approve-${request.id}`}
                          >
                            {processingRequest === request.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                <Check className="w-3 h-3 mr-1" />
                                承認
                              </>
                            )}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white h-8"
                            onClick={() => handleLeaveRequestAction(request.id, "rejected")}
                            disabled={processingRequest === request.id}
                            data-testid={`reject-${request.id}`}
                          >
                            <X className="w-3 h-3 mr-1" />
                            却下
                          </Button>
                        </div>
                      )}
                      {request.status !== "pending" && request.approved_at && (
                        <div className="text-xs text-slate-500">
                          {request.status === "approved" ? "承認" : "却下"}日: {new Date(request.approved_at).toLocaleDateString('ja-JP')}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Face Registration Dialog */}
      <Dialog open={showFaceDialog} onOpenChange={closeFaceDialog}>
        <DialogContent className="max-w-2xl bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>顔登録 - {selectedEmployee?.name}</DialogTitle>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            {/* Camera */}
            <div className="space-y-3">
              {/* カメラ選択 */}
              <div className="flex gap-2">
                <Button 
                  variant={cameraFacing === "user" ? "default" : "outline"} 
                  size="sm" 
                  className={`flex-1 ${cameraFacing === "user" ? "bg-emerald-600" : "border-slate-600"}`}
                  onClick={() => switchCamera("user")}
                >
                  <Camera className="w-4 h-4 mr-1" /> フロント
                </Button>
                <Button 
                  variant={cameraFacing === "environment" ? "default" : "outline"} 
                  size="sm" 
                  className={`flex-1 ${cameraFacing === "environment" ? "bg-emerald-600" : "border-slate-600"}`}
                  onClick={() => switchCamera("environment")}
                >
                  <Camera className="w-4 h-4 mr-1" /> バック
                </Button>
              </div>

              <div className="relative aspect-[4/3] bg-black rounded-lg overflow-hidden">
                <video ref={videoRef} autoPlay playsInline muted className={`w-full h-full object-cover ${cameraFacing === "user" ? "scale-x-[-1]" : ""}`} />
                <canvas ref={overlayCanvasRef} className={`absolute top-0 left-0 w-full h-full ${cameraFacing === "user" ? "scale-x-[-1]" : ""}`} />
                <canvas ref={canvasRef} className="hidden" />
                
                {/* ステータス */}
                <div className="absolute bottom-2 left-2 right-2">
                  <div className={`px-3 py-1.5 rounded text-xs font-medium text-center ${faceDetected ? 'bg-emerald-600' : 'bg-slate-700'}`}>
                    {faceDetected ? '顔を検出しました - 撮影可能' : '顔を検出していません'}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Input value={faceLabel} onChange={(e) => setFaceLabel(e.target.value)} placeholder="ラベル（任意）例: 正面、メガネあり" className="bg-slate-700 border-slate-600 text-sm" />
                <Button onClick={captureFace} className={`w-full ${faceDetected ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-600'}`} disabled={capturing || !faceDetected}>
                  {capturing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />登録中...</> : <><Camera className="w-4 h-4 mr-2" />撮影して登録</>}
                </Button>
              </div>
            </div>

            {/* Registered Faces */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-sm">登録済み</h3>
                <Badge variant="outline" className="border-slate-600">{registeredFaces.length}枚</Badge>
              </div>

              {registeredFaces.length === 0 ? (
                <div className="text-center py-8 text-slate-400 bg-slate-700 rounded-lg">
                  <Image className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">まだ登録されていません</p>
                </div>
              ) : (
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2 pr-2">
                    {registeredFaces.map((face, index) => (
                      <div key={face.id} className="flex items-center justify-between p-2 bg-slate-700 rounded-lg">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-400" />
                          <div>
                            <p className="text-sm">{face.label || `顔 ${index + 1}`}</p>
                            <p className="text-xs text-slate-400">{face.created_at ? new Date(face.created_at).toLocaleDateString('ja-JP') : ''}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => deleteFace(face.id)} className="text-red-400 hover:text-red-300 h-7">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}

              <div className="p-3 bg-slate-700 rounded-lg text-xs text-slate-300">
                <p className="font-medium mb-1">複数枚登録で認識精度UP</p>
                <p className="text-slate-400">様々な角度・照明条件で撮影してください</p>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2 border-t border-slate-700">
            <Button variant="outline" onClick={closeFaceDialog} className="border-slate-600">閉じる</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Paid Leave Management Dialog */}
      <Dialog open={showPaidLeaveDialog} onOpenChange={setShowPaidLeaveDialog}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white" data-testid="paid-leave-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              有給管理 - {selectedEmployee?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 bg-slate-700 rounded-lg">
                <p className="text-xs text-slate-400 mb-1">年間付与</p>
                <p className="text-2xl font-bold text-emerald-400">{paidLeaveData.granted}</p>
                <p className="text-xs text-slate-400">日</p>
              </div>
              <div className="p-3 bg-slate-700 rounded-lg">
                <p className="text-xs text-slate-400 mb-1">使用済み</p>
                <p className="text-2xl font-bold text-orange-400">{paidLeaveData.used}</p>
                <p className="text-xs text-slate-400">日</p>
              </div>
              <div className="p-3 bg-slate-700 rounded-lg">
                <p className="text-xs text-slate-400 mb-1">残日数</p>
                <p className="text-2xl font-bold text-blue-400">{paidLeaveData.balance}</p>
                <p className="text-xs text-slate-400">日</p>
              </div>
            </div>

            <div className="space-y-3 pt-2 border-t border-slate-700">
              <div className="space-y-2">
                <Label>年間付与日数</Label>
                <Input 
                  type="number" 
                  step="0.5"
                  value={paidLeaveData.granted} 
                  onChange={(e) => setPaidLeaveData({ ...paidLeaveData, granted: parseFloat(e.target.value) || 0 })}
                  className="bg-slate-700 border-slate-600"
                  data-testid="paid-leave-granted-input"
                />
              </div>
              <div className="space-y-2">
                <Label>残日数（調整）</Label>
                <Input 
                  type="number" 
                  step="0.5"
                  value={paidLeaveData.balance} 
                  onChange={(e) => setPaidLeaveData({ ...paidLeaveData, balance: parseFloat(e.target.value) || 0 })}
                  className="bg-slate-700 border-slate-600"
                  data-testid="paid-leave-balance-input"
                />
                <p className="text-xs text-slate-400">※ 繰越や特別付与時に手動調整できます</p>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowPaidLeaveDialog(false)} className="flex-1 border-slate-600" data-testid="paid-leave-cancel-btn">
                キャンセル
              </Button>
              <Button onClick={savePaidLeave} disabled={savingPaidLeave} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="paid-leave-save-btn">
                {savingPaidLeave ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                保存
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ID Document Management Dialog */}
      <Dialog open={showIdDocDialog} onOpenChange={setShowIdDocDialog}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white" data-testid="id-document-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              身分証明書管理 - {selectedEmployee?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Company Name Display */}
            {companyName && (
              <div className="flex items-center gap-2 p-3 bg-slate-700 rounded-lg">
                <Building2 className="w-4 h-4 text-blue-400" />
                <span className="text-sm">所属会社: <span className="font-medium text-white">{companyName}</span></span>
              </div>
            )}

            {/* Existing Documents */}
            <div className="space-y-2">
              <Label>登録済み証明書</Label>
              {idDocuments.length === 0 ? (
                <div className="p-4 bg-slate-700 rounded-lg text-center text-slate-400 text-sm">
                  登録された証明書はありません
                </div>
              ) : (
                <div className="space-y-2">
                  {idDocuments.map((doc) => {
                    const expiryDate = doc.expiry_date || doc.expiry;
                    const isExpired = expiryDate && new Date(expiryDate) < new Date();
                    const isExpiringSoon = expiryDate && !isExpired && new Date(expiryDate) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                    return (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{doc.type}</span>
                            {isExpired && (
                              <Badge className="bg-red-600 text-xs">期限切れ</Badge>
                            )}
                            {isExpiringSoon && (
                              <Badge className="bg-yellow-600 text-xs">期限間近</Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-400 mt-1">番号: {doc.number}</p>
                          {expiryDate && (
                            <p className={`text-xs mt-1 ${isExpired ? 'text-red-400' : isExpiringSoon ? 'text-yellow-400' : 'text-slate-400'}`}>
                              有効期限: {new Date(expiryDate).toLocaleDateString('ja-JP')}
                            </p>
                          )}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => deleteIdDocument(doc.id)} className="text-red-400 hover:text-red-300 h-8">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Add New Document */}
            <div className="space-y-3 pt-2 border-t border-slate-700">
              <Label>新規追加</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-400">種類</Label>
                  <Select value={newIdDoc.type} onValueChange={(v) => setNewIdDoc({ ...newIdDoc, type: v })}>
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="在留カード">在留カード</SelectItem>
                      <SelectItem value="特別永住者証明書">特別永住者証明書</SelectItem>
                      <SelectItem value="パスポート">パスポート</SelectItem>
                      <SelectItem value="運転免許証">運転免許証</SelectItem>
                      <SelectItem value="マイナンバーカード">マイナンバーカード</SelectItem>
                      <SelectItem value="健康保険証">健康保険証</SelectItem>
                      <SelectItem value="その他">その他</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-400">有効期限</Label>
                  <Input 
                    type="date" 
                    value={newIdDoc.expiry} 
                    onChange={(e) => setNewIdDoc({ ...newIdDoc, expiry: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-slate-400">証明書番号 *</Label>
                <Input 
                  value={newIdDoc.number} 
                  onChange={(e) => setNewIdDoc({ ...newIdDoc, number: e.target.value })}
                  placeholder="例: AB12345678CD"
                  className="bg-slate-700 border-slate-600"
                  data-testid="id-doc-number-input"
                />
              </div>
              <Button onClick={addIdDocument} disabled={savingIdDoc || !newIdDoc.number} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="id-doc-add-btn">
                {savingIdDoc ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                追加
              </Button>
            </div>

            <div className="flex justify-end pt-2 border-t border-slate-700">
              <Button variant="outline" onClick={() => setShowIdDocDialog(false)} className="border-slate-600" data-testid="id-doc-close-btn">
                閉じる
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Capture Dialog */}
      <Dialog open={showPhotoDialog} onOpenChange={closePhotoDialog}>
        <DialogContent className={`bg-slate-800 border-slate-700 text-white ${photoType === 'profile' ? 'max-w-sm' : 'max-w-lg'}`} data-testid="photo-capture-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              {getPhotoLabel(photoType)}を撮影
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!capturedPhoto ? (
              <>
                <div className={`relative bg-black rounded-lg overflow-hidden ${photoType === 'profile' ? 'aspect-[3/4]' : 'aspect-[4/3]'}`}>
                  <video 
                    ref={photoVideoRef} 
                    autoPlay 
                    playsInline 
                    muted 
                    className="w-full h-full object-cover"
                  />
                  {photoType === 'profile' && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-32 h-40 border-2 border-white/50 rounded-lg"></div>
                    </div>
                  )}
                </div>
                <Button 
                  onClick={capturePhoto} 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="capture-photo-btn"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  撮影
                </Button>
              </>
            ) : (
              <>
                <div className={`relative bg-black rounded-lg overflow-hidden ${photoType === 'profile' ? 'aspect-[3/4]' : 'aspect-[4/3]'}`}>
                  <img 
                    src={capturedPhoto} 
                    alt="撮影した写真" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={retakePhoto} 
                    className="flex-1 border-slate-600"
                    data-testid="retake-photo-btn"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    撮り直す
                  </Button>
                  <Button 
                    onClick={savePhoto} 
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="save-photo-btn"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    この写真を使用
                  </Button>
                </div>
              </>
            )}
            <canvas ref={photoCanvasRef} className="hidden" />
          </div>
          <div className="flex justify-end pt-2 border-t border-slate-700">
            <Button variant="outline" onClick={closePhotoDialog} className="border-slate-600">
              キャンセル
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR一括印刷確認ダイアログ */}
      <Dialog open={showBulkQrDialog} onOpenChange={setShowBulkQrDialog}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-purple-400" />
              QRコード一括印刷
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-300">
              全従業員（{employees.filter(e => e.is_active !== false).length}名）のQRコードを印刷します。
            </p>
            <div className="p-4 bg-slate-700/50 rounded-lg">
              <p className="text-sm text-slate-400 mb-2">印刷内容：</p>
              <ul className="text-sm text-slate-300 space-y-1">
                <li>・従業員名</li>
                <li>・従業員コード</li>
                <li>・打刻用QRコード</li>
              </ul>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowBulkQrDialog(false)} className="flex-1 border-slate-600">
                キャンセル
              </Button>
              <Button onClick={printBulkQrCodes} className="flex-1 bg-purple-600 hover:bg-purple-700">
                <Printer className="w-4 h-4 mr-2" />
                印刷する
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeesPage;
